import React, { ReactNode, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Svg, { G, Path, SvgXml } from 'react-native-svg';
import { FACE_ID_ICON_SVG } from '../../assets/securityRecommendationSvgs';
import { PASSWORD_SHIELD_ICON_SVG } from '../../assets/passwordShieldSvg';
import type { BiometryType } from '../../services/biometrics';
import { getBiometryDisplayName } from '../../services/biometrics';
import { COLORS } from '../../theme/themeTokens';

type SupportedBiometryType = Exclude<BiometryType, null>;

interface RecommendationLayoutProps {
  illustration: ReactNode;
  title: string;
  subtitle: string;
  primaryLabel: string;
  onPrimary: () => void;
  onLater: () => void;
  loading?: boolean;
  error?: string | null;
}

const RecommendationLayout: React.FC<RecommendationLayoutProps> = ({
  illustration,
  title,
  subtitle,
  primaryLabel,
  onPrimary,
  onLater,
  loading = false,
  error,
}) => (
  <ScrollView
    style={styles.container}
    contentContainerStyle={styles.content}
    bounces={false}
    showsVerticalScrollIndicator={false}>
    <View style={styles.hero}>
      <View style={styles.illustration} accessibilityElementsHidden>
        {illustration}
      </View>

      <View style={styles.textContainer}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.subtitle}>{subtitle}</Text>
      </View>

      <View style={styles.actions}>
        {error ? (
          <Text style={styles.error} accessibilityRole="alert">
            {error}
          </Text>
        ) : null}

        <TouchableOpacity
          style={[styles.primaryButton, loading && styles.buttonDisabled]}
          onPress={onPrimary}
          disabled={loading}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel={primaryLabel}>
          {loading ? (
            <ActivityIndicator color={COLORS.white} />
          ) : (
            <Text style={styles.primaryButtonText}>{primaryLabel}</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.laterButton}
          onPress={onLater}
          disabled={loading}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Do it later">
          <Text style={[styles.laterButtonText, loading && styles.textDisabled]}>
            Do it later
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  </ScrollView>
);

const FaceIdIllustration = () => (
  <SvgXml xml={FACE_ID_ICON_SVG} width={90} height={90} />
);

const FingerprintIllustration = () => (
  <Svg width={96} height={96} viewBox="0 0 96 96">
    <G
      fill="none"
      stroke={COLORS.primaryNavy}
      strokeWidth={2.2}
      strokeLinecap="round">
      <Path d="M29 39c3-10 10-16 19-16 12 0 21 10 21 23 0 17-6 29-14 38" />
      <Path d="M22 50v-4c0-17 11-30 26-30 16 0 28 13 28 30 0 19-6 34-13 44" />
      <Path d="M35 48v-3c0-8 5-14 13-14s14 6 14 15c0 16-5 26-12 36" />
      <Path d="M28 56c0 13-2 20-6 27" />
      <Path d="M42 48c0-4 2-7 6-7s7 3 7 7c0 12-4 21-11 30" />
      <Path d="M35 60c-1 12-4 20-8 27" />
      <Path d="M48 51c0 9-3 16-8 23" />
    </G>
    <Path
      d="M18 32V20h12M66 20h12v12M18 64v12h12M66 76h12V64"
      fill="none"
      stroke={COLORS.brandOrange}
      strokeWidth={1.6}
      strokeLinecap="round"
    />
  </Svg>
);

const ShieldIllustration = () => (
  <SvgXml xml={PASSWORD_SHIELD_ICON_SVG} width={90} height={90} />
);

interface BiometricRecommendationScreenProps {
  biometryType: SupportedBiometryType;
  onEnable: () => Promise<boolean>;
  onLater: () => void;
}

export const BiometricRecommendationScreen: React.FC<BiometricRecommendationScreenProps> = ({
  biometryType,
  onEnable,
  onLater,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const biometryName = getBiometryDisplayName(biometryType);

  const handleEnable = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const enabled = await onEnable();
      if (!enabled) {
        setError(`Couldn't enable ${biometryName}. Try again or do it later.`);
      }
    } catch {
      setError(`Couldn't enable ${biometryName}. Try again or do it later.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RecommendationLayout
      illustration={biometryType === 'FaceID' ? <FaceIdIllustration /> : <FingerprintIllustration />}
      title={`Turn on ${biometryName}?`}
      subtitle={`Turn on ${biometryName} to log into apps faster`}
      primaryLabel={`Turn on ${biometryName}`}
      onPrimary={handleEnable}
      onLater={onLater}
      loading={loading}
      error={error}
    />
  );
};

interface PasswordRecommendationScreenProps {
  onOpenSettings: () => void;
  onLater: () => void;
}

export const PasswordRecommendationScreen: React.FC<PasswordRecommendationScreenProps> = ({
  onOpenSettings,
  onLater,
}) => (
  <RecommendationLayout
    illustration={<ShieldIllustration />}
    title="Change Password"
    subtitle="Recommendation to change the pass for your security"
    primaryLabel="Go to settings"
    onPrimary={onOpenSettings}
    onLater={onLater}
  />
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  content: {
    flexGrow: 1,
    backgroundColor: COLORS.white,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hero: {
    width: '100%',
    maxWidth: 370,
    height: 334,
    alignItems: 'center',
    transform: [{ translateY: -120 }],
  },
  illustration: {
    width: 90,
    height: 90,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 40,
  },
  textContainer: {
    width: '100%',
    height: 60,
    alignItems: 'center',
  },
  title: {
    fontFamily: 'Inter',
    width: '100%',
    height: 28,
    fontSize: 24,
    lineHeight: 28,
    fontWeight: '500',
    color: COLORS.brandOrange,
    textAlign: 'center',
  },
  subtitle: {
    width: '100%',
    height: 20,
    marginTop: 12,
    fontFamily: 'Inter',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '400',
    color: COLORS.primaryNavy,
    textAlign: 'center',
  },
  actions: {
    width: '100%',
    height: 104,
    marginTop: 40,
  },
  error: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: -30,
    paddingHorizontal: 8,
    fontFamily: 'Inter',
    fontSize: 14,
    lineHeight: 19,
    color: COLORS.brandOrange,
    textAlign: 'center',
  },
  primaryButton: {
    width: '100%',
    height: 52,
    borderRadius: 8,
    backgroundColor: COLORS.primaryNavy,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '500',
    color: COLORS.white,
  },
  laterButton: {
    width: '100%',
    height: 52,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  laterButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '500',
    color: COLORS.primaryNavy,
  },
  buttonDisabled: {
    opacity: 0.65,
  },
  textDisabled: {
    opacity: 0.5,
  },
});
